import { useState, useEffect, useMemo } from "react";
import { useSalon } from "../context/SalonContext";
import { useApp } from "../context/AppContext";
import { useProducts } from "../context/ProductsContext";
import { useOnboarding } from "../context/OnboardingContext";
import {
  isProgressiveOnboardingComplete,
  OnboardingProgressResponse,
} from "../components/onboarding/types/onboarding";
import { normalizeDepositConfigsResponse } from "../utils/depositConfigUtils";

/**
 * Returns whether the Onboarding nav item and page should be shown.
 * Used by NavBar to show/hide the Onboarding link. No redirect from /onboarding when all complete.
 *
 * When progressive onboarding is complete (Growth mode), we hide the dashboard
 * if the user has both products and deposits configured (no cards left to show).
 *
 * Fetches deposit configs only when we don't have a value yet (depositConfigCount === null).
 * OnboardingDashboard updates the same context when it fetches on mount, so the nav item
 * updates when the user navigates to onboarding without refetching on every URL change.
 */
export function useOnboardingDashboardVisibility(): {
  showNavItem: boolean;
  isLoading: boolean;
} {
  const { businessInfo, onboardingProgress } = useSalon() as {
    businessInfo?: { id: string } | null;
    onboardingProgress: OnboardingProgressResponse | null;
  };
  const { products } = useProducts();
  const { depositConfigCount, setDepositConfigCount } = useOnboarding();
  const { getDepositConfigs } = useApp();
  const [isLoading, setIsLoading] = useState(true);

  const isFullyComplete = useMemo(
    () =>
      onboardingProgress
        ? isProgressiveOnboardingComplete(onboardingProgress)
        : false,
    [onboardingProgress],
  );

  const hasProducts = (products?.length ?? 0) > 0;

  // Fetch only when we need an initial value AND we're in Growth mode (isFullyComplete).
  // In Setup mode (!isFullyComplete), showNavItem is always true, so we don't need the deposit count here.
  // The Dashboard will set the count when it fetches; we must NOT overwrite it.
  useEffect(() => {
    if (!businessInfo?.id) {
      setIsLoading(false);
      return;
    }
    if (!isFullyComplete) {
      // In Setup mode, we don't need the deposit count for visibility decisions — just skip.
      // DO NOT reset depositConfigCount here; the Dashboard sets it.
      setIsLoading(false);
      return;
    }
    if (depositConfigCount !== null) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getDepositConfigs(businessInfo.id)
      .then((response) => {
        if (cancelled) return;
        const list = normalizeDepositConfigsResponse(response);
        setDepositConfigCount(list.length);
      })
      .catch(() => {
        if (!cancelled) setDepositConfigCount(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    businessInfo?.id,
    isFullyComplete,
    getDepositConfigs,
    depositConfigCount,
    setDepositConfigCount,
  ]);

  const showNavItem = useMemo(() => {
    if (!onboardingProgress) return true;
    if (!isFullyComplete) return true;
    if (depositConfigCount === null) return true;
    const hasDeposits = depositConfigCount > 0;
    const hasAnySecondaryCard = !hasDeposits || !hasProducts;
    return hasAnySecondaryCard;
  }, [onboardingProgress, isFullyComplete, depositConfigCount, hasProducts]);

  return { showNavItem, isLoading };
}
